import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styles from '../WeeklyBreakdownCharts.module.scss';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';
import { Spinner } from 'components/UI';
import type { WeeklyBreakdownReport, WeekDayKey } from 'model/BookingsWeeklyBreakdownReport';

type Slice = { name: string; value: number; percentage: number; color: string };
type StatusKey = 'completed' | 'canceled' | 'noShow';

const COLORS = ['#BDD2CA', '#92B4A7', '#5E8677', '#BAA997', '#DCCCBB', '#EAB464', '#D5E1DB'];
const DAY_KEYS: WeekDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const STATUS_OPTIONS: Array<SelectOption<null>> = [
    { label: 'Completed', value: 'completed' },
    { label: 'Canceled', value: 'canceled' },
    { label: 'No-show', value: 'noShow' }
];

interface Props {
    loading: boolean;
    report: WeeklyBreakdownReport;
}

export const QuantityByStatusChart: React.FC<Props> = ({ loading, report }) => {
    const [status, setStatus] = React.useState<StatusKey>('completed');

    const data: Slice[] = React.useMemo(() => {
        const row = report?.[status];
        if (!row?.values) return [];

        return DAY_KEYS.map((k, i) => {
            const dayLabel = report.days?.[k]?.format('ddd, [[]MM/DD/YYYY[]]') ?? `${k}`;
            const v = row.values[k]?.value ?? 0;
            const pct = row.values[k]?.pct ?? 0;

            return {
                name: dayLabel,
                value: v,
                percentage: Number(pct),
                color: COLORS[i % COLORS.length]
            };
        });
    }, [report, status]);

    const total = React.useMemo(() => data.reduce((s, d) => s + (d.value ?? 0), 0), [data]);

    return (
        <div className={styles.card}>
            <div className={styles.headerRow}>
                <h3 className={styles.title}>Quantity by Status</h3>

                <Select
                    className={styles.inlineSelect}
                    options={STATUS_OPTIONS}
                    value={status}
                    onChange={val => setStatus(val as StatusKey)}
                    placeholder="Select status"
                />
            </div>

            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loading}>
                        <Spinner className={styles.spinner} />
                    </div>
                ) : (
                    <>
                        <div className={styles.pieWrap}>
                            <ResponsiveContainer
                                width="100%"
                                height={200}
                            >
                                <PieChart>
                                    <Pie
                                        data={data}
                                        dataKey="value"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        startAngle={90}
                                        endAngle={450}
                                        stroke="none"
                                    >
                                        {data.map((s, i) => (
                                            <Cell
                                                key={i}
                                                fill={s.color}
                                            />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>

                            <div className={styles.centerLabel}>
                                <div className={styles.totalValue}>{total}</div>
                                <div className={styles.totalPercentage}>100%</div>
                            </div>
                        </div>

                        <div className={styles.legend}>
                            {data.map((s, i) => (
                                <div
                                    key={i}
                                    className={styles.legendItem}
                                >
                                    <div
                                        className={styles.legendDot}
                                        style={{ backgroundColor: s.color }}
                                    />
                                    <span className={styles.legendLabel}>{s.name}</span>
                                    <span className={styles.legendValue}>
                                        {s.value} ({s.percentage}%)
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default QuantityByStatusChart;
